import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  filter,
  firstValueFrom,
  map,
  mergeMap,
  of,
  switchMap,
  takeUntil,
  combineLatest,
} from 'rxjs';

import { createEffectFactory } from '@recruitee/ngrx';
import { RecordsResponse } from '@recruitee/views-types';

import { ViewsHttpService } from '../../services/views-http.service';
import { ViewRecordsActions } from './records.store';

@Injectable({ providedIn: 'root' })
export class RecordsEffects {
  public effect = createEffectFactory(ViewRecordsActions, this.actions$);

  public fetchOne$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ViewRecordsActions.fetch),
      mergeMap(({ payload: { recordsId, dataSourceId, data, queryParams, storeOptions } }) =>
        this.viewsHttpService.fetchRecords(dataSourceId, data, queryParams).pipe(
          takeUntil(
            this.actions$.pipe(
              ofType(ViewRecordsActions.fetch),
              filter(({ payload }) => payload.recordsId === recordsId),
            ),
          ),
          map(response =>
            ViewRecordsActions.fetchSuccess({
              payload: {
                data: { ...response, id: recordsId, params: data },
                concat: storeOptions?.concat ?? false,
              },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(ViewRecordsActions.fetchFailed({ payload: { error, id: recordsId } })),
          ),
        ),
      ),
    ),
  );

  public fetchViewAllRecordsCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ViewRecordsActions.fetchViewAllRecordsCount),
      switchMap(({ payload: { recordsId, dataSourceId, data } }) =>
        this.viewsHttpService
          .fetchLayoutRecordsCount(dataSourceId, data, { page: 1, limit: 0 })
          .pipe(
            map(response =>
              ViewRecordsActions.fetchViewAllRecordsCountSuccess({
                payload: {
                  data: { ...response, id: recordsId, params: { ...data, group: null } },
                },
              }),
            ),
            catchError((error: HttpErrorResponse) =>
              of(
                ViewRecordsActions.fetchViewAllRecordsCountFailed({
                  payload: { error, id: recordsId },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  public refresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ViewRecordsActions.refresh),
      mergeMap(({ payload: { recordsId, dataSourceId, data, meta } }) => {
        const maxRecordsPerPage = 1000;
        const records = meta.page * meta.limit;
        const requestCount = Math.ceil(records / maxRecordsPerPage);
        const requests = [...Array(requestCount)].map((_, index) => {
          const limit =
            records - maxRecordsPerPage * index > maxRecordsPerPage
              ? maxRecordsPerPage
              : records % maxRecordsPerPage;
          return this.viewsHttpService.fetchRecords(dataSourceId, data, {
            page: index + 1,
            limit,
          });
        });

        return combineLatest(requests).pipe(
          map(responses => {
            const records = responses.flatMap(response => response.records);
            return ViewRecordsActions.refreshSuccess({
              payload: {
                data: { id: recordsId, meta, records, params: data },
              },
            });
          }),
        );
      }),
    ),
  );

  public fetchAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ViewRecordsActions.fetchAll),
      mergeMap(({ payload: { recordsId, dataSourceId, data, uuid } }) =>
        of(null).pipe(
          takeUntil(
            this.actions$.pipe(
              ofType(ViewRecordsActions.fetchAll, ViewRecordsActions.fetch),
              filter(({ payload }) => payload.recordsId === recordsId),
            ),
          ),
          switchMap(async () => {
            // TODO
            /**
             * This is a temporary solution to fetch all records.
             * Backend changes are required to remove this workaround.
             */
            const limit = 1000;
            let page = 1;
            let allLoadel = false;
            let response: RecordsResponse | null = null;

            while (!allLoadel) {
              const res = await firstValueFrom(
                this.viewsHttpService.fetchRecords(dataSourceId, data, { page, limit }),
              );
              response = response
                ? { ...res, records: [...response.records, ...res.records] }
                : res;
              if (response.meta.totalCount < limit * page) {
                allLoadel = true;
              } else {
                page = page + 1;
              }
            }

            return ViewRecordsActions.fetchAllSuccess({
              payload: { uuid, data: { ...response!, id: recordsId, params: data } },
            });
          }),
          catchError((error: HttpErrorResponse) =>
            of(ViewRecordsActions.fetchAllFailed({ payload: { error, id: recordsId, uuid } })),
          ),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private viewsHttpService: ViewsHttpService,
  ) {}
}
